import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { ArrowLeft, Save, Shield, Package, CheckCircle2, Circle } from 'lucide-react';
import { useUser, useUserProducts, useUserEntitlements, useUpdateUser, useUpdateUserStatus } from '../../hooks/useUsers';
import { useUrnResolver } from '../../hooks/useUrnResolver';
import PageHeader from '../../components/common/PageHeader';
import UserStatusDialog from './UserStatusDialog';
import SuccessToast from '../../components/common/SuccessToast';
import type { UserStatus } from '../../api/users';
import { formatDate } from '../../utils/date';

export default function UserDetailPage() {
  const { urn } = useParams<{ urn: string }>();
  const decodedUrn = decodeURIComponent(urn!);
  const navigate = useNavigate();
  const { data: user, isLoading, error } = useUser(decodedUrn);
  const { data: userProducts } = useUserProducts(decodedUrn);
  const { data: entitlements } = useUserEntitlements(decodedUrn);
  const updateUser = useUpdateUser();
  const updateUserStatus = useUpdateUserStatus();
  const [statusToConfirm, setStatusToConfirm] = useState<UserStatus | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const { resolveProductUrn } = useUrnResolver();

  const { register, handleSubmit } = useForm();

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="text-center py-20">
        <p className="text-red-500 dark:text-red-400 mb-2">Failed to load user</p>
        <button onClick={() => navigate('/users')} className="btn-secondary">Back to Users</button>
      </div>
    );
  }

  const onSubmit = (values: Record<string, unknown>) => {
    updateUser.mutate({ urn: decodedUrn, data: values });
  };

  const isActive = user.status !== 'blocked';
  const displayName = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.name || user.email;
  const isDeactivating = statusToConfirm === 'blocked';

  const confirmStatus = async () => {
    if (!statusToConfirm || updateUserStatus.isPending) {
      return;
    }
    setStatusError(null);
    try {
      await updateUserStatus.mutateAsync({ urn: decodedUrn, status: statusToConfirm });
      setStatusToConfirm(null);
      setNotice(isDeactivating ? 'Success: User Deactivated' : 'Success: User Activated');
    } catch {
      setStatusError(`Unable to ${isDeactivating ? 'deactivate' : 'activate'} this user. Please try again.`);
    }
  };

  return (
    <div>
      <PageHeader
        title={user.name || user.email}
        description={user.urn}
        actions={
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-1.5 text-[13px] text-content">
              {isActive
                ? <CheckCircle2 aria-hidden="true" className="h-3.5 w-3.5 fill-brand text-white" />
                : <Circle aria-hidden="true" className="h-3.5 w-3.5 text-content-muted" />}
              {isActive ? 'Active' : 'Inactive'}
            </span>
            <button
              type="button"
              aria-haspopup="dialog"
              disabled={updateUserStatus.isPending}
              onClick={() => {
                setStatusError(null);
                setNotice(null);
                setStatusToConfirm(isActive ? 'blocked' : 'active');
              }}
              className={`inline-flex items-center justify-center rounded-lg border bg-surface px-4 py-2 text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand disabled:opacity-50 ${isActive ? 'border-red-500 text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30' : 'border-brand text-brand hover:bg-surface-sunken'}`}
            >
              {isActive ? 'Deactivate' : 'Activate'}
            </button>
            <button type="button" onClick={() => navigate('/users')} className="btn-secondary gap-1">
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
          </div>
        }
      />

      <div className="max-w-4xl space-y-4 lg:space-y-6">
        {/* User Info */}
        <form onSubmit={handleSubmit(onSubmit)} className="card p-4 lg:p-6">
          <h2 className="text-base lg:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 lg:mb-4">User Details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4 mb-4">
            <div>
              <label className="label">Name</label>
              <input type="text" className="input" defaultValue={user.name} {...register('name')} />
            </div>
            <div>
              <label className="label">Email</label>
              <input type="text" className="input bg-gray-50 dark:bg-gray-700" value={user.email} disabled />
            </div>
            <div>
              <label className="label">First Name</label>
              <input type="text" className="input" defaultValue={user.firstName || ''} {...register('firstName')} />
            </div>
            <div>
              <label className="label">Last Name</label>
              <input type="text" className="input" defaultValue={user.lastName || ''} {...register('lastName')} />
            </div>
            <div>
              <label className="label">Created</label>
              <input type="text" className="input bg-gray-50 dark:bg-gray-700" value={formatDate(user.creationDate)} disabled />
            </div>
            <div>
              <label className="label">Updated</label>
              <input type="text" className="input bg-gray-50 dark:bg-gray-700" value={formatDate(user.modificationDate)} disabled />
            </div>
          </div>
          <div className="flex justify-end">
            <button type="submit" disabled={updateUser.isPending} className="btn-primary gap-1">
              <Save className="h-4 w-4" />
              Save Changes
            </button>
          </div>
        </form>

        {/* Privileges */}
        <div className="card p-4 lg:p-6">
          <h2 className="text-base lg:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 lg:mb-4 flex items-center gap-2">
            <Shield className="h-5 w-5 text-gray-400" />
            Privileges
          </h2>
          {(!user.privileges || user.privileges.length === 0) ? (
            <p className="text-sm text-gray-400 dark:text-gray-500">No privileges assigned.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {user.privileges.map((priv, idx) => (
                <span key={idx} className="badge bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">{priv}</span>
              ))}
            </div>
          )}
        </div>

        {/* Profiles */}
        {user.profiles && user.profiles.length > 0 && (
          <div className="card p-4 lg:p-6">
            <h2 className="text-base lg:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 lg:mb-4">Profiles</h2>
            <div className="space-y-3">
              {user.profiles.map((profile, idx) => (
                <div key={idx} className="p-3 rounded bg-gray-50 dark:bg-gray-700/50">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">{profile.name}</p>
                  <div className="flex flex-wrap gap-1">
                    {profile.privileges.map((priv, i) => (
                      <span key={i} className="badge bg-gray-200 text-gray-600 dark:bg-gray-600 dark:text-gray-300 text-[10px]">{priv}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Entitlements */}
        <div className="card p-4 lg:p-6">
          <h2 className="text-base lg:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 lg:mb-4 flex items-center gap-2">
            <Shield className="h-5 w-5 text-gray-400" />
            Entitlements
          </h2>
          {(!entitlements || entitlements.length === 0) ? (
            <p className="text-sm text-gray-400 dark:text-gray-500">No entitlements found.</p>
          ) : (
            <div className="space-y-1">
              {entitlements.map((ent, idx) => (
                <div key={idx} className="flex items-center justify-between px-3 py-2 rounded bg-gray-50 dark:bg-gray-700/50">
                  <span className="text-sm text-gray-700 dark:text-gray-200">{ent.name}</span>
                  <span className="font-mono text-xs text-gray-400 dark:text-gray-500">{ent.urn}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Products */}
        <div className="card p-4 lg:p-6">
          <h2 className="text-base lg:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 lg:mb-4 flex items-center gap-2">
            <Package className="h-5 w-5 text-gray-400" />
            Products
          </h2>
          {(!userProducts || userProducts.length === 0) ? (
            <p className="text-sm text-gray-400 dark:text-gray-500">No products assigned.</p>
          ) : (
            <div className="space-y-1">
              {userProducts.map((product, idx) => {
                const urn = typeof product === 'string' ? product : JSON.stringify(product);
                return (
                  <div key={idx} className="flex items-center gap-2 px-3 py-2 rounded bg-gray-50 dark:bg-gray-700/50">
                    <span className="text-sm text-gray-700 dark:text-gray-200" title={urn}>
                      {typeof product === 'string' ? resolveProductUrn(product) : JSON.stringify(product)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {statusToConfirm && (
        <UserStatusDialog
          userName={displayName}
          status={statusToConfirm}
          isPending={updateUserStatus.isPending}
          errorMessage={statusError}
          onConfirm={confirmStatus}
          onCancel={() => {
            setStatusToConfirm(null);
            setStatusError(null);
          }}
        />
      )}
      {notice && <SuccessToast message={notice} onDismiss={() => setNotice(null)} />}
    </div>
  );
}
